/**
 * pdpAccess.js — shared PDP decisions for ICOD/TVOD purchase bundles.
 *
 * The Spotlight API reports locks per entity: a locked single channel carries
 * subscription_access { unlocked: false, purchasable: true } itself, while a
 * parent wrapper stays unlocked and the lock lives on its child (season)
 * channels. The PDP CTA must consider both levels.
 */

/** Locked-and-purchasable = TVOD/ICOD paywall (vs. locked-unavailable). */
export const isTVODLocked = sub => sub?.unlocked === false && sub?.purchasable === true;

/**
 * Decide the PDP main CTA.
 *
 * @param {Object|null} channel - top-level PDP channel
 * @param {Object|null} currentChildChannel - selected season, if any
 * @returns {{action: 'buy', channelToBuy: Object} | {action: 'watch'}}
 */
export function resolvePdpCta(channel, currentChildChannel) {
  if (isTVODLocked(channel?.subscription_access)) {
    return { action: 'buy', channelToBuy: channel };
  }
  if (isTVODLocked(currentChildChannel?.subscription_access)) {
    return { action: 'buy', channelToBuy: currentChildChannel };
  }
  return { action: 'watch' };
}

/**
 * Effective TVOD lock for surfaces that only have a /subscriptions/check
 * response (e.g. search results, hover modals): the channel is locked itself,
 * OR it is a wrapper whose child channels are ALL TVOD locked. Mixed parents
 * (some seasons free) are NOT effectively locked - their CTA stays Watch Now.
 */
export function isEffectivelyTVODLocked(sub) {
  if (isTVODLocked(sub)) {return true;}
  const children = sub?.childchannels;
  return Array.isArray(children) && children.length > 0 && children.every(isTVODLocked);
}

/**
 * Product id of the first TVOD-locked child in a /subscriptions/check
 * response - the checkout target when the lock lives on a season rather
 * than the channel the user clicked.
 */
export function lockedChildProductId(sub) {
  const children = sub?.childchannels;
  if (!Array.isArray(children)) {return null;}
  const locked = children.find(isTVODLocked);
  return locked ? locked.dspro_id || locked._id || null : null;
}

/**
 * Playback gate for an episode on a watch page. Locked when the channel is
 * TVOD locked directly, the episode's season is locked, or the channel is a
 * fully locked wrapper - unless the episode itself carries bypass_channel_lock
 * (trailers/promos that always play).
 *
 * @param {Object} args
 * @param {Object|null} args.episode - the episode/video about to play
 * @param {Object|null} args.seasonSub - subscription_access of the episode's season
 * @param {Object|null} args.channelSub - channel-level subscription data
 *   (embedded subscription_access or a /subscriptions/check response)
 */
export function isEpisodePlaybackLocked({ episode, seasonSub, channelSub }) {
  if (episode?.bypass_channel_lock === true || episode?.video?.bypass_channel_lock === true) {
    return false;
  }
  return isTVODLocked(channelSub) || isTVODLocked(seasonSub) || isEffectivelyTVODLocked(channelSub);
}

/**
 * Content item for a single channel (no child channels, direct video) so the
 * PDP can render its content row — with a lock overlay when TVOD locked.
 * Returns null for parent channels (the episodes section covers those).
 */
export function getSingleChannelContent(channel) {
  if (!channel || !channel.video) {return null;}
  if (Array.isArray(channel.childchannels) && channel.childchannels.length > 0) {return null;}

  const video = channel.video;
  const thumbnail =
    channel.videos_thumb ||
    (video.thumb ? `https://images.dotstudiopro.com/${video.thumb}` : null);

  return {
    _id: video._id,
    title: video.title || channel.title,
    thumbnail,
    duration: video.duration ? parseFloat(video.duration) : null,
  };
}
